
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import VideoConference from "@/components/digital-event/VideoConference";
import EventControls from "@/components/digital-event/EventControls";
import Participants from "@/components/digital-event/Participants";
import Chat from "@/components/digital-event/Chat";
import AIFeatures from "@/components/digital-event/AIFeatures";
import LiveStreamingSetup from "@/components/digital-event/LiveStreamingSetup";
import { Loader2 } from "lucide-react";

interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  role: "host" | "attendee" | "moderator";
}

export interface Message {
  id: string;
  userId: string;
  userName: string;
  content: string;
  timestamp: Date;
  type: "text" | "file" | "system";
}

const DigitalEvent = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [isHost, setIsHost] = useState(false);
  const [eventData, setEventData] = useState<any>(null);
  const [participants, setParticipants] = useState<User[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isLiveStreaming, setIsLiveStreaming] = useState(false);
  const [activeTab, setActiveTab] = useState("participants");
  const [localUser, setLocalUser] = useState<User | null>(null);

  // Mock data for demonstration
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        // In a real implementation, this would be an API call
        const mockEvent = {
          id: eventId,
          title: "Annual Tech Conference",
          description: "Join us for the latest trends and innovations in technology.",
          startTime: new Date(),
          endTime: new Date(Date.now() + 2 * 60 * 60 * 1000), // 2 hours
          host: {
            id: "user-1",
            name: "Alex Johnson",
            email: "alex@example.com",
            avatar: "https://i.pravatar.cc/150?img=1",
            role: "host" as const
          }
        };

        const mockParticipants = [
          mockEvent.host,
          {
            id: "user-2",
            name: "Sarah Williams",
            email: "sarah@example.com",
            avatar: "https://i.pravatar.cc/150?img=5",
            role: "moderator" as const
          },
          {
            id: "user-3",
            name: "Michael Chen",
            email: "michael@example.com",
            avatar: "https://i.pravatar.cc/150?img=8",
            role: "attendee" as const
          },
          {
            id: "user-4",
            name: "Emma Davis",
            email: "emma@example.com",
            avatar: "https://i.pravatar.cc/150?img=9",
            role: "attendee" as const
          }
        ];

        // Get user from localStorage
        const userData = localStorage.getItem('user');
        const user = userData ? JSON.parse(userData) : null;
        
        // Set a default mock user if not found
        const currentUser = user ? {
          id: "user-1", // Setting as host for demo purposes
          name: user.name || user.email,
          email: user.email,
          avatar: user.avatar || "https://i.pravatar.cc/150?img=1",
          role: "host" as const
        } : mockEvent.host;
        
        setLocalUser(currentUser);
        setIsHost(currentUser.id === mockEvent.host.id);
        setEventData(mockEvent);
        setParticipants(mockParticipants);
        
        const initialMessages: Message[] = [
          {
            id: "msg-1",
            userId: "system",
            userName: "System",
            content: "Welcome to the Annual Tech Conference virtual meeting!",
            timestamp: new Date(Date.now() - 5 * 60 * 1000),
            type: "system"
          },
          {
            id: "msg-2",
            userId: mockEvent.host.id,
            userName: mockEvent.host.name,
            content: "Hello everyone! We'll be starting in a few minutes.",
            timestamp: new Date(Date.now() - 2 * 60 * 1000),
            type: "text"
          }
        ];
        
        setMessages(initialMessages);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching event:", error);
        toast({
          title: "Error loading event",
          description: "Could not load the event data. Please try again.",
          variant: "destructive"
        });
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId, toast]);

  const handleSendMessage = (content: string) => {
    if (!localUser) return;
    
    const newMessage: Message = {
      id: `msg-${Date.now()}`,
      userId: localUser.id,
      userName: localUser.name,
      content,
      timestamp: new Date(),
      type: "text"
    };
    
    setMessages((prev) => [...prev, newMessage]);
  };

  const toggleRecording = () => {
    setIsRecording(!isRecording);
    toast({
      title: isRecording ? "Recording stopped" : "Recording started",
      description: isRecording ? "Your recording has been saved." : "Recording this meeting...",
    });
  };

  const toggleLiveStreaming = (platform?: string) => {
    setIsLiveStreaming(!isLiveStreaming);
    toast({
      title: isLiveStreaming ? "Live stream ended" : `Live streaming to ${platform || 'public page'}`,
      description: isLiveStreaming ? "Your live stream has ended." : "Your event is now live!",
    });
  };

  const leaveEvent = () => {
    toast({
      title: "Left the event",
      description: "You have successfully left the event.",
    });
    navigate("/dashboard");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-cecure-700" />
        <span className="ml-2 text-lg">Loading event...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      
      <div className="flex-grow flex flex-col md:flex-row h-[calc(100vh-64px)]">
        <div className="md:w-3/4 h-full flex flex-col">
          <div className="p-4 bg-white shadow-sm flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold">{eventData?.title}</h1>
              <p className="text-sm text-gray-500">
                {isRecording && <span className="text-red-500 mr-2">● Recording</span>}
                {isLiveStreaming && <span className="text-green-500 mr-2">● Live</span>}
                {new Date(eventData?.startTime).toLocaleTimeString()} - {new Date(eventData?.endTime).toLocaleTimeString()}
              </p>
            </div>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm" onClick={leaveEvent}>Leave</Button>
            </div>
          </div>
          
          <div className="flex-grow p-4 bg-gray-100">
            <VideoConference participants={participants} localUser={localUser} />
          </div>
          
          <div className="p-4 bg-white shadow-sm border-t">
            <EventControls 
              isHost={isHost} 
              isRecording={isRecording} 
              isLiveStreaming={isLiveStreaming}
              onToggleRecording={toggleRecording}
              onToggleLiveStreaming={toggleLiveStreaming}
            />
          </div>
        </div>
        
        <div className="md:w-1/4 h-full bg-white border-l">
          <Tabs defaultValue={activeTab} className="h-full flex flex-col" onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-3 mx-4 mt-4">
              <TabsTrigger value="participants">People</TabsTrigger>
              <TabsTrigger value="chat">Chat</TabsTrigger>
              <TabsTrigger value="ai">AI</TabsTrigger>
            </TabsList>
            
            <TabsContent value="participants" className="flex-grow overflow-auto p-4">
              <Participants participants={participants} isHost={isHost} />
            </TabsContent>
            
            <TabsContent value="chat" className="flex-grow flex flex-col h-full">
              <Chat messages={messages} onSendMessage={handleSendMessage} user={localUser} />
            </TabsContent>
            
            <TabsContent value="ai" className="flex-grow overflow-auto p-4">
              <AIFeatures isHost={isHost} isRecording={isRecording} />
            </TabsContent>
          </Tabs>
          
          {isHost && activeTab === "ai" && isLiveStreaming && (
            <div className="p-4 border-t">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Live Streaming</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-xs text-gray-500">
                    Your event is being streamed. View analytics and manage settings in the AI tab.
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          {isHost && activeTab === "participants" && (
            <div className="p-4 border-t">
              <LiveStreamingSetup 
                isLiveStreaming={isLiveStreaming} 
                onToggleLiveStreaming={toggleLiveStreaming} 
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DigitalEvent;
